import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoSeenc from 'src/assets/images/seenc/seenc_logo (1).svg';
import { FlatIcon, FlatIconOptions, Image } from 'src/components/seenc/ui';
import { SeencLoginButton } from 'src/components/seenc/ui/SeencLoginButton';
import { SeencTextInput } from 'src/components/seenc/ui/SeencTextInput';
import { useAuth, useMounted, useSearchParams } from 'src/hooks';
import useAdornmentWidth from 'src/hooks/useAdormentWidth';
import { createSeencTheme } from 'src/theme/seenc';
import { getMobileOperatingSystem, sleep } from 'src/utils/getUtils';
import * as Yup from 'yup';

export default function AuthenticationLoginPage() {
  const navigate = useNavigate();
  const theme = createSeencTheme();

  const { signIn } = useAuth();
  const isMounted = useMounted();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  const linkWhatsAppComercial =
    'https://api.whatsapp.com/send?phone=5516993286681&text=Ol%C3%A1%2C%20quero%20saber%20mais%20sobre%20o%20Seenc%20WMS.';

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Insira um e-mail válido').required('Este campo é obrigatório'),
      password: Yup.string().min(6, 'Sua senha deve ter no mínimo 6 caracteres').required('Este campo é obrigatório'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        await signIn(values.email, values.password);
        if (isMounted()) {
          navigate('/app/', { replace: true });
        }
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({
          submit: err?.response?.data?.message || 'Erro ao fazer login',
        });
        helpers.setSubmitting(false);
      }
    },
  });

  const adornmentWidth = useAdornmentWidth({
    deps: [formik.values],
  });

  useEffect(() => {
    sleep(500).then(async () => {
      const email = searchParams.get('email');
      const password = searchParams.get('password');
      if (!email || !password) return;

      try {
        await signIn(email, password);
        if (isMounted()) {
          navigate('/app/', { replace: true });
        }
      } catch {}
    });
  }, []);

  const styles = {
    bannerContainer: {
      backgroundImage: `url('https://s3.sa-east-1.amazonaws.com/assets-s3.weesutech.com.br/apps/seenc/login/banners/banner1.png')`,
    },
  };

  return (
    <Grid
      container
      sx={{
        width: '100dvw',
        height: '100dvh',
        backgroundColor: theme.palette.background.primary,
        display: 'flex',
        flexDirection: 'row',
      }}>
      <Grid
        item
        xs={0}
        md={6}
        sx={{
          backgroundColor: '#dbe8f1',
          backgroundImage: styles.bannerContainer.backgroundImage,
          backgroundRepeat: 'no-repeat',
          backgroundSize: {
            xs: 'contain',
            md: 'contain',
            lg: 'contain',
            xl: 'initial',
          },
          backgroundPosition: 'center',
          aspectRatio: '16/9',
          display: { xs: 'none', md: 'flex' },
          cursor: 'pointer',
        }}
        onClick={() => {
          window.open('https://weesu.com.br/seenc-wms', '_blank', 'noopener,noreferrer');
        }}
      />
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          padding: theme.custom.padding.default,
          backgroundColor: theme.palette.background.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            width: {
              xs: '100%',
              xl: '60%',
            },
          }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Image
              src={logoSeenc}
              alt='Seenc WMS logo'
              style={{ width: 280, height: 100 }}
            />
          </Box>
          <Card
            component={Paper}
            elevation={4}
            sx={{
              width: '100%',
              borderRadius: theme.custom.borderRadius.default,
              mt: 3,
            }}>
            <CardHeader title='Acesse sua conta' />
            <CardContent>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={theme.custom.gap.default}>
                  <SeencTextInput
                    label='Email'
                    autoFocus
                    error={!!(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    placeholder='Email'
                    name='email'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type='email'
                    value={formik.values.email}
                    adornmentWidth={adornmentWidth}
                    trackAdornmentWidth
                  />
                  <SeencTextInput
                    error={!!(formik.touched.password && formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    label='Senha'
                    placeholder='Senha'
                    name='password'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPassword((prevState) => !prevState)}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                            color='primary'>
                            {showPassword ?
                              <FlatIcon icon={FlatIconOptions.noVisible} />
                            : <FlatIcon icon={FlatIconOptions.visible} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    adornmentWidth={adornmentWidth}
                    trackAdornmentWidth
                  />
                </Stack>
                {formik.errors.submit && (
                  <Typography
                    color='error'
                    sx={{ mt: 3 }}
                    variant='seenc_default'>
                    {formik.errors.submit}
                  </Typography>
                )}
                <SeencLoginButton
                  type='submit'
                  disabled={!formik.isValid}
                  loading={formik.isSubmitting}
                  sx={{ mt: 3 }}>
                  Entrar
                </SeencLoginButton>
              </form>
            </CardContent>
          </Card>
          <Box
            sx={{
              marginTop: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              verticalAlign: 'center',
            }}>
            <Typography
              color={theme.palette.common.white}
              variant='seenc_default'
              sx={{
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '20px',
                fontSize: getMobileOperatingSystem() !== 'unknown' ? '14px' : '16px',
              }}>
              <FlatIcon
                icon={FlatIconOptions.transport}
                style={{ color: theme.palette.primary.main, fontSize: '20px', marginRight: '10px' }}
              />
              <Link
                component='a'
                href='/transportadora'
                style={{ color: theme.palette.common.white, textDecoration: 'none' }}>
                Sou transportadora quero agendar entrega
              </Link>
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              textAlign: 'center',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 2,
              gap: 0.5,
            }}>
            <Typography
              color={theme.palette.common.white}
              variant='seenc_default'
              style={{ fontSize: getMobileOperatingSystem() !== 'unknown' ? '14px' : '16px' }}>
              Ainda não é cliente?
            </Typography>
            <Link
              component='a'
              size='large'
              href={linkWhatsAppComercial}
              target='_blank'
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                display: 'flex',
                justifyContent: 'center',
                gap: 0.5,
              }}>
              <Typography sx={{ fontSize: getMobileOperatingSystem() !== 'unknown' ? '14px' : '16px' }}>
                Fale com um especialista
              </Typography>
              <FlatIcon
                icon={FlatIconOptions.arrowRight}
                size={getMobileOperatingSystem() !== 'unknown' ? '13px' : '16px'}
              />
            </Link>
          </Box>
          <Box
            sx={{
              marginTop: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}></Box>
          <Box
            sx={{
              padding: 1,
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: { xs: '100%', xl: '50%' },
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', xl: 'row' },
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: { xs: 'center', xl: 'flex-end' },
                textAlign: 'right',
                gap: 0.5,
              }}>
              <Typography
                color={theme.palette.primary.main}
                variant='seenc_default'>
                <Link
                  component='a'
                  href='https://weesu.com.br/politica-de-privacidade-weesu/'
                  target='_blank'
                  style={{
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                    fontSize: '12px',
                  }}>
                  Política de privacidade
                </Link>
              </Typography>
              <Typography
                color={theme.palette.common.white}
                style={{ fontSize: '12px' }}>
                •
              </Typography>
              <Typography
                color={theme.palette.primary.main}
                variant='seenc_default'>
                <Link
                  component='a'
                  href='https://ajuda.weesu.com.br/'
                  target='_blank'
                  style={{
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                    fontSize: '12px',
                  }}>
                  Central de ajuda
                </Link>
              </Typography>
            </Box>

            <Box
              sx={{
                verticalAlign: 'bottom',
                alignContent: 'center',
                textAlign: { xs: 'center', xl: 'none' },
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: { xs: 'center', xl: 'flex-end' } }}>
                <Box>
                  <Typography
                    color={theme.palette.common.white}
                    variant='seenc_default'
                    style={{
                      fontSize: '11px',
                    }}>
                    © {new Date().getFullYear()} Weesu Tecnologia. Todos os direitos reservados.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
